import {
  ArrowRight,
  Briefcase,
  Clock,
  DollarSign,
  Heart,
  MapPin,
} from "lucide-react";
import jobPostingApi from "../../api/jobPosting";
import * as Sentry from "@sentry/react";

const JobSearchCard = ({
  job,
  onNavigate,
  isSaved,
  onSaveToggle,
  hasSubscription,
}) => {
  const timeSince = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const seconds = Math.floor((new Date() - date) / 1000);
    let interval = seconds / 86400;
    if (interval > 1) return `${Math.floor(interval)} days ago`;
    return "Today";
  };

  const formatSalary = (min, max) => {
    if (!min && !max) return "Competitive Salary";
    const format = (num) => `$${(parseFloat(num) / 1000).toFixed(0)}K`;
    if (min && max) return `${format(min)} - ${format(max)}`;
    return `Up to ${format(min || max)}`;
  };

  const handleJobView = async (id) => {
    try {
      await jobPostingApi.trackJobView(id);
    } catch (error) {
      // console.error(error);
      Sentry.captureException(error, "Error tracking job view");
    }
  };

  return (
    <div
      className="group bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl hover:border-primary-300 transition-all duration-300 cursor-pointer relative overflow-hidden"
      onClick={() => {
        onNavigate(`/job-details/${job.id}`);
        handleJobView(job.id);
      }}
    >
      <div className="absolute top-0 left-0 h-full w-1.5 bg-primary-500 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
      {/* Urgent Chip - Top Right Corner */}
      {job.is_urgent && (
        <div className="absolute top-0 right-3 z-10">
          <span className="bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded-full animate-pulse">
            Urgent Hiring
          </span>
        </div>
      )}
      <div className="flex items-start space-x-4">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center font-bold text-primary-600 text-xl flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
          {job.cover_image ? (
            <img
              src={job.cover_image}
              alt={job.company_name}
              className="w-full h-full object-cover rounded-md"
            />
          ) : (
            <span>
              {hasSubscription
                ? job.company_name?.charAt(0).toUpperCase()
                : "AC"}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-semibold text-primary-600 mb-1">
                {hasSubscription ? job.company_name : "Aviation Company"}
              </p>
              <h3 className="text-lg font-bold text-secondary-900 group-hover:text-primary-700 line-clamp-2">
                {job.title}
              </h3>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSaveToggle();
              }}
              className={`p-2 rounded-full transition-all duration-200 ${
                isSaved
                  ? "text-red-500 bg-red-100"
                  : "text-secondary-400 opacity-0 group-hover:opacity-100 hover:bg-secondary-100"
              }`}
            >
              <Heart className={`w-5 h-5 ${isSaved ? "fill-current" : ""}`} />
            </button>
          </div>
          <div className="flex flex-wrap items-center text-sm text-secondary-600 gap-x-4 gap-y-1 mt-3">
            <span className="flex items-center">
              <MapPin size={14} className="mr-1.5" />
              {job.location}
            </span>
            <span className="flex items-center">
              <Briefcase size={14} className="mr-1.5" />
              {job.job_type}
            </span>
            <span className="flex items-center">
              <Clock size={14} className="mr-1.5" />
              {timeSince(job.created_at)}
            </span>
          </div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
        <span className="text-green-600 font-semibold text-sm flex items-center">
          <DollarSign size={14} className="mr-1.5" />
          {formatSalary(job.salary_min, job.salary_max)}
        </span>
        <span className="text-primary-600 font-semibold text-sm flex items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          View Details <ArrowRight size={14} className="ml-1" />
        </span>
      </div>
    </div>
  );
};

export default JobSearchCard;
